import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  AlertTitle,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../config/axiosConfig';

const PASSWORD_RULE = /^[A-Za-z0-9]{8}$/;
const PASSWORD_HELP = 'Debe tener exactamente 8 caracteres, solo números y letras, sin símbolos especiales.';

// Misma pantalla para dos ligas de un solo uso: "Olvidé mi contraseña" (reset) y la
// activación del correo de bienvenida (activation), que además deja la sesión iniciada.
const COPY = {
  reset: {
    title: 'Restablecer contraseña',
    intro: null,
    endpoint: '/users/reset-password',
    submit: 'Restablecer contraseña',
    invalidLink: 'El enlace de recuperación es inválido o ya venció. Solicita uno nuevo desde la pantalla de inicio de sesión.',
  },
  activation: {
    title: 'Activa tu cuenta',
    intro: 'Crea la contraseña con la que entrarás al Portal CONEM. Debe tener exactamente 8 caracteres, solo letras y números (sin espacios, acentos ni símbolos) y distingue mayúsculas de minúsculas. Al guardarla entrarás directamente al portal.',
    endpoint: '/users/activate-account',
    submit: 'Crear contraseña y entrar',
    invalidLink: 'La liga de activación es inválida, ya se usó o venció. Usa «¿Olvidaste tu contraseña?» en la pantalla de inicio de sesión o pide a CONEM que te reenvíe la invitación.',
  },
};

const ResetPassword = ({ mode = 'reset' }) => {
  const { token } = useParams();
  const navigate = useNavigate();
  const copy = COPY[mode] || COPY.reset;

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!PASSWORD_RULE.test(newPassword)) {
      setError(PASSWORD_HELP);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post(copy.endpoint, { token, newPassword });
      if (mode === 'activation' && response.data?.token) {
        sessionStorage.setItem('authToken', response.data.token);
        // Recarga completa: la app arranca con la sesión nueva y carga sus permisos.
        window.location.assign('/home');
        return;
      }
      setSuccess(true);
    } catch (err) {
      setError(typeof err.response?.data === 'string' ? err.response.data : copy.invalidLink);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
      <Paper elevation={3} style={{ padding: '2rem', width: '100%', borderRadius: '24px' }}>
        <Box
          sx={{ position: 'absolute', top: 16, left: 16, zIndex: 3, display: 'flex', alignItems: 'center' }}
        >
          <img src="/logo.ico" alt="Conem Logo" style={{ height: 75 }} />
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" component="h1" gutterBottom align="center">
            {copy.title}
          </Typography>
          <hr />
          {copy.intro && !success && (
            <Alert severity="info" sx={{ mt: 2 }}>{copy.intro}</Alert>
          )}

          {success ? (
            <>
              <Alert severity="success" sx={{ mt: 2 }}>
                <AlertTitle>Contraseña actualizada</AlertTitle>
                Tu contraseña se actualizó correctamente. Ya puedes iniciar sesión con ella.
              </Alert>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                sx={{ mt: 2 }}
                onClick={() => navigate('/login')}
              >
                Ir a iniciar sesión
              </Button>
            </>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                margin="normal"
                label="Nueva contraseña"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                helperText={PASSWORD_HELP}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                margin="normal"
                label="Confirmar contraseña"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                required
              />

              {error && (
                <Alert severity="error" style={{ marginTop: '1rem' }}>
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                style={{ marginTop: '1rem' }}
                disabled={loading}
              >
                {loading ? 'Guardando...' : copy.submit}
              </Button>

              <Box style={{ marginTop: '1rem', textAlign: 'center' }}>
                <Button
                  variant="text"
                  onClick={() => navigate('/login')}
                  sx={{ textTransform: 'none', textDecoration: 'underline', p: 0, minWidth: 'auto' }}
                >
                  Volver a iniciar sesión
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default ResetPassword;
